import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Trash2, Download } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Logs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const fetchLogs = useCallback(async () => {
    try {
      const params = filter !== "all" ? { level: filter.toUpperCase() } : {};
      const res = await axios.get(`${API}/logs`, { params: { ...params, limit: 200 } });
      setLogs(res.data);
    } catch (error) {
      console.error("Error fetching logs:", error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchLogs();
    const interval = setInterval(fetchLogs, 5000);
    return () => clearInterval(interval);
  }, [fetchLogs]);

  const handleClear = async () => {
    try {
      await axios.delete(`${API}/logs`);
      toast.success("Logs cleared");
      fetchLogs();
    } catch (error) {
      toast.error("Failed to clear logs");
    }
  };

  const handleExport = () => {
    const content = logs.map(log => 
      `[${log.created_at}] [${log.level}] ${log.message}${log.details ? ` - ${log.details}` : ''}`
    ).join('\n');
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `seo-autopilot-logs-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'ERROR': return 'text-red-400';
      case 'WARNING': return 'text-yellow-400';
      case 'INFO': return 'text-blue-400';
      default: return 'text-zinc-400';
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-full">
        <RefreshCw className="h-6 w-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="logs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-mono font-medium text-white">Logs</h1>
          <p className="text-sm text-zinc-500">System activity and run logs</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-32 bg-zinc-800 border-zinc-700 text-white" data-testid="log-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="info">Info</SelectItem>
              <SelectItem value="warning">Warning</SelectItem>
              <SelectItem value="error">Error</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            data-testid="export-logs-btn"
          >
            <Download className="h-4 w-4 mr-1" />
            Export
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleClear}
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-red-400"
            data-testid="clear-logs-btn"
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Clear
          </Button>
        </div>
      </div>

      {/* Logs Viewer */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="border-b border-zinc-800">
          <CardTitle className="text-sm font-mono text-zinc-300">
            Log Entries ({logs.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="bg-black h-[calc(100vh-280px)] overflow-y-auto font-mono text-xs" data-testid="logs-viewer">
            {logs.map((log, idx) => (
              <div 
                key={log.id || idx}
                className="flex items-start gap-4 px-4 py-2 border-b border-zinc-900 hover:bg-zinc-900/50"
              >
                <span className="text-zinc-600 whitespace-nowrap">
                  {new Date(log.created_at).toLocaleString()}
                </span>
                <span className={`font-semibold whitespace-nowrap w-16 ${getLevelColor(log.level)}`}>
                  [{log.level}]
                </span>
                <span className="text-zinc-300 flex-1">{log.message}</span>
                {log.run_id && (
                  <span className="text-zinc-700 whitespace-nowrap">
                    run:{log.run_id.slice(0, 8)}
                  </span>
                )}
              </div>
            ))}
            {logs.length === 0 && (
              <div className="flex items-center justify-center h-32 text-zinc-600">
                No logs to display
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
